/*
  <summary>
    This functional component provides functionalities like redirection to other modules, logout & User Profile.
    Developer: Aashish Singh, Created Date: 29-March-2023
  </summary>
  <returns>Returns JSX</returns>
*/

import { observer } from "mobx-react";
import React, { useEffect, useRef, useState } from "react";
import { FormattedMessage, injectIntl } from "react-intl";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import Huckster_Small_icon from "../../assets/Images/Huckster_Small_icon.png";
import mainLogo from "../../assets/Images/logo--white.png";
import userTypeEnum from "../../constants/enums/user-type-enum";
import { useStore } from "../../contexts/store-provider";
import { getIsTenantAccessFromLocalStorage, getStoreIdFromLocalStorage, getUserTypeFromLocalStorage } from "../../helpers/localstorage-helper";
import LogoutPopup from "../../modules/auth/components/logout-popup";
import LogoutPopupHOC from "../../modules/auth/hoc/logout-popup-hoc";
import UserProfilePopupHOC from "../../modules/auth/hoc/user-profile-popup-hoc";
import UserProfilePopup from "../../modules/auth/user-profile-popup";
import BusinessTypeSettings from "../../modules/master-management/master-settings/business-type-settings/business-type-settings";
import TaxSettings from "../../modules/master-management/master-settings/tax-settings/tax-settings";
import TimeSettings from "../../modules/master-management/master-settings/time-settings/time-settings";
import { formatMessage } from "../../translations/format-message";
import BusinessTypeSettingPopupHOC from "../hoc/business-type-popup-hoc";
import TaxSettingPopupHOC from "../hoc/tax-setting-popup-hoc";
import TimeSettingPopupHOC from "../hoc/time-setting-popup-hoc";
import RoutesConstants from "../routes/routes-constants";
import "./customer-left-navbar.css";
import "./left-navbar.css";

// Props interface for the component
interface IProps {
  changeLanguage: (lang: string) => void;
  showTimeSettingPopup: boolean;
  timeSettingPopupToggleHandler: () => void;
  showTaxSettingPopup: boolean;
  taxSettingPopupToggleHandler: () => void;
  showBusinessTypeSettingPopup: boolean;
  businessTypeSettingPopupToggleHandler: () => void;
  showLogoutPopup: boolean;
  logoutPopupToggleHandler: () => void;
}

// Main CustomerLeftNavBar component
const CustomerLeftNavBar = (props: any) => {
  // Hooks and stores
  const history = useHistory();
  const location = useLocation();
  const { preferencesStore, authStore, customerManagementStore } = useStore();
  const myRef = useRef<any>();
  const { getUserType, getEmail, userType, setShowLogoutPopup, showLogoutPopup, } = authStore;

  // Determine user type for menu rendering
  const isHucksterUser =
    (getUserType === userTypeEnum[0] ||
      getUserType === userTypeEnum[1] ||
      getUserType === userTypeEnum[4]) &&
    getIsTenantAccessFromLocalStorage() == false;

  const isCompany =
    (getUserType === userTypeEnum[2] || getUserType === userTypeEnum[3]) &&
    getIsTenantAccessFromLocalStorage() == false;

  const { GetCustomerByIdService, customerDetail } = customerManagementStore;

  // State for settings popup
  const [showSettingsPopup, setShowSettingsPopup] = useState(false);

  // Get customerId from props or localStorage
  const customerId = props.id ?? localStorage.getItem("lizera_userId");

  // Fetch customer details on mount or customerId change
  useEffect(() => {
    if (customerId) {
      // let storeId = getStoreIdFromLocalStorage();
      GetCustomerByIdService(customerId);
    }
  }, [customerId, GetCustomerByIdService]);

  // Toggle sidebar open/close
  const handleChange = () => {
    const div = document.getElementById("left-sidebarNav");
    const move = document.getElementById("moveContent");
    if (div) {
      if (!div.className.includes("closeNav")) {
        div.classList.add("closeNav");
        if (move) move.classList.remove("ShiftLeftSide");
      } else {
        div.classList.remove("closeNav");
        if (move) move.classList.add("ShiftLeftSide");
      }
    }
  };

  // Close sidebar
  const handleChange2 = () => {
    const div = document.getElementById("left-sidebarNav");
    const move = document.getElementById("moveContent");
    if (div !== null) {
      div.classList.add("closeNav");
      if (move) move.classList.remove("ShiftLeftSide");
    }
  };

  // Close sidebar on outside click (currently not used)
  const closeOnOutsideClick = () => {
    var div = document.getElementById("left-sidebarNav");
    const move = document.getElementById("moveContent");
    if (div) {
      if (!div.className.includes("closeNav")) {
        div.classList.add("closeNav");
        if (move) move.classList.remove("ShiftLeftSide");
      }
    }
  };

  // Handler for outside click (currently commented out)
  const handleClickOutside = (e: any) => {
    if (!myRef.current.contains(e.target)) {
      // closeOnOutsideClick();
    }
  };

  // Add/remove event listener for outside click
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  
  return (
    <React.Fragment>
      {/* Popups for settings and user actions */}
      {props.showTimeSettingPopup && (
        <TimeSettings modalClosed={() => props.timeSettingPopupToggleHandler()} />
      )}
      {props.showBusinessTypeSettingPopup && (
        <BusinessTypeSettings modalClosed={() => props.businessTypeSettingPopupToggleHandler()} />
      )}
      {props.showTaxSettingPopup && (
        <TaxSettings modalClosed={() => props.taxSettingPopupToggleHandler()} />
      )}
      {props.showUserProfilePopup && (
        <UserProfilePopup
          userName={props.email}
          userType={props.userType}
          modalClosed={props.UserProfileToggleHandler}
        />
      )}
      {/* {props.showLogoutPopup && (
        <LogoutPopup modalClosed={() => props.logoutPopupToggleHandler()} />
      )} */}

      {showLogoutPopup && (
                      <LogoutPopup modalClosed={() => setShowLogoutPopup(!showLogoutPopup)} />
                  )}

      {/* Sidebar navigation */}
      <aside
        id="customer-sidebarNav"
        ref={myRef}
        className={`${
          getIsTenantAccessFromLocalStorage() == true &&
          getUserType === userTypeEnum[4]
            ? "primary-navbar"
            : ""
        } closeNav`}
      >
        <div className="nav-inner">
          <nav>
            {/* Sidebar header with logo */}
            <div className="sidebar-header">
              <div
                className="sidebar-logo"
                title={formatMessage("application_title")}
              >
                <img src={mainLogo} className="openNav-logo img-fluid" />
                <img src={Huckster_Small_icon} className="closeNav-logo img-fluid" />
              </div>
            </div>
            {/* Sidebar menu */}
            <ul className="list-unstyled">
              {isHucksterUser && (
                <React.Fragment>
                  {/* Customer name */}
                  <li className="profile_name">
                    <h4 className="p-3">
                      {customerDetail
                        ? `${customerDetail.LastNameKana} ${customerDetail.FirstNameKana}`
                        
                        : ""}
                    </h4>
                  </li>
                  {/* Reservation menu item */}
                  <li title={formatMessage("reservation")}>
                    <NavLink to={RoutesConstants.ReservationList}>
                      <span className="svgWidth">
                        {/* Reservation icon */}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="22"
                          height="22"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M8 1.25a.75.75 0 0 1 .75.75v3a.75.75 0 0 1-1.5 0V2A.75.75 0 0 1 8 1.25ZM16 1.25a.75.75 0 0 1 .75.75v3a.75.75 0 0 1-1.5 0V2a.75.75 0 0 1 .75-.75Z"
                            fill="currentColor"
                          />
                          <path
                            opacity=".4"
                            d="M21.5 8.37v8.76c0 .16-.01.32-.02.47H2.52c-.01-.15-.02-.31-.02-.47V8.37A4.87 4.87 0 0 1 7.37 3.5h9.26a4.87 4.87 0 0 1 4.87 4.87Z"
                            fill="currentColor"
                          />
                          <path
                            d="M21.48 17.602a4.876 4.876 0 0 1-4.85 4.4H7.37c-2.53 0-4.61-1.93-4.85-4.4h18.96ZM13.53 11.622c.45-.31.73-.77.73-1.39 0-1.3-1.04-1.97-2.26-1.97-1.22 0-2.27.67-2.27 1.97 0 .62.29 1.09.73 1.39-.61.36-.96.94-.96 1.62 0 1.24.95 2.01 2.5 2.01 1.54 0 2.5-.77 2.5-2.01 0-.68-.35-1.27-.97-1.62ZM12 9.502c.52 0 .9.29.9.79 0 .49-.38.8-.9.8s-.9-.31-.9-.8c0-.5.38-.79.9-.79Zm0 4.5c-.66 0-1.14-.33-1.14-.93 0-.6.48-.92 1.14-.92.66 0 1.14.33 1.14.92 0 .6-.48.93-1.14.93Z"
                            fill="currentColor"
                          />
                        </svg>
                      </span>
                      <span className="nav-text">
                        <FormattedMessage id="reservation" />
                      </span>
                    </NavLink>
                  </li>
                  {/* Booking management menu item */}
                  <li title={formatMessage("booking_management")}>
                    <NavLink to={RoutesConstants.LizeraStoreList}>
                      <span className="svgWidth">
                        {/* Booking management icon */}
                        <svg version="1.1" id="Layer_1" width="22"
                          height="22" x="0px" y="0px"
                            viewBox="0 0 32 32">
                          <g id="Booking">
                            <path fill="currentColor" opacity="0.4" d="M24.5,17.6c-3,0-5.5,2.5-5.5,5.5s2.5,5.5,5.5,5.5s5.5-2.5,5.5-5.5S27.5,17.6,24.5,17.6z M26.9,22.8L24.3,25
                              c-0.2,0.1-0.4,0.2-0.6,0.2c0,0-0.1,0-0.1,0c-0.2,0-0.5-0.1-0.6-0.3l-1.1-1.3c-0.3-0.4-0.2-1,0.2-1.3c0.4-0.3,1-0.2,1.3,0.2l0.5,0.6
                              l2-1.6c0.4-0.3,1-0.2,1.3,0.1C27.4,21.9,27.3,22.5,26.9,22.8z"/>
                            <path fill="currentColor" d="M17.5,23.1c0-3.9,3.1-7,7-7c1.3,0,2.5,0.4,3.5,1V8c0-2.3-1.8-4.1-4.1-4.1h-2.2V3.4c0-0.5-0.4-0.9-0.9-0.9
                              c-0.5,0-0.9,0.4-0.9,0.9v0.5H9.2V3.4c0-0.5-0.4-0.9-0.9-0.9S7.4,2.9,7.4,3.4v0.5H5.2C2.9,3.9,1.1,5.7,1.1,8v14.7
                              c0,2.3,1.8,4.1,4.1,4.1h13.4C17.9,25.8,17.5,24.5,17.5,23.1z M21,12.2c0.6,0,1,0.5,1,1s-0.5,1-1,1s-1-0.5-1-1S20.5,12.2,21,12.2z
                              M8.1,20c-0.6,0-1-0.5-1-1s0.5-1,1-1s1,0.5,1,1S8.7,20,8.1,20z M8.1,14.3c-0.6,0-1-0.5-1-1s0.5-1,1-1s1,0.5,1,1S8.7,14.3,8.1,14.3z
                              M14.6,20c-0.6,0-1-0.5-1-1s0.5-1,1-1s1,0.5,1,1S15.1,20,14.6,20z M14.6,14.3c-0.6,0-1-0.5-1-1s0.5-1,1-1s1,0.5,1,1
                              S15.1,14.3,14.6,14.3z"/>
                          </g>
                          </svg>
                      </span>
                      <span className="nav-text">
                        <FormattedMessage id="booking_management" />
                      </span>
                    </NavLink>
                  </li>
                  {/* Profile menu item */}
                  <li title={formatMessage("profile")}>
                    <NavLink to={RoutesConstants.Profile}>
                      <span className="svgWidth svg-icon">
                        {/* Profile icon */}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            opacity=".4"
                            d="M9 2C6.38 2 4.25 4.13 4.25 6.75c0 2.57 2.01 4.65 4.63 4.74.08-.01.16-.01.22 0h.07a4.738 4.738 0 0 0 4.58-4.74C13.75 4.13 11.62 2 9 2Z"
                            fill="currentColor"
                          />
                          <path
                            d="M14.08 14.149c-2.79-1.86-7.34-1.86-10.15 0-1.27.85-1.97 2-1.97 3.23s.7 2.37 1.96 3.21c1.4.94 3.24 1.41 5.08 1.41 1.84 0 3.68-.47 5.08-1.41 1.26-.85 1.96-1.99 1.96-3.23-.01-1.23-.7-2.37-1.96-3.21Z"
                            fill="currentColor"
                          />
                          <path
                            opacity=".4"
                            d="M19.99 7.338c.16 1.94-1.22 3.64-3.13 3.87h-.05c-.06 0-.12 0-.17.02-.97.05-1.86-.26-2.53-.83 1.03-.92 1.62-2.3 1.5-3.8a4.64 4.64 0 0 0-.77-2.18 3.592 3.592 0 0 1 5.15 2.92Z"
                            fill="currentColor"
                          />
                          <path
                            d="M21.988 16.59c-.08.97-.7 1.81-1.74 2.38-1 .55-2.26.81-3.51.78.72-.65 1.14-1.46 1.22-2.32.1-1.24-.49-2.43-1.67-3.38-.67-.53-1.45-.95-2.3-1.26 2.21-.64 4.99-.21 6.7 1.17.92.74 1.39 1.67 1.3 2.63Z"
                            fill="currentColor"
                          />
                        </svg>
                      </span>
                      <span className="nav-text">
                        <FormattedMessage id="profile" />
                      </span>
                    </NavLink>
                  </li>
                  {/* Logout menu item */}
                  <li title={formatMessage("logout")}>
                    <a href="javascript:;" onClick={() => setShowLogoutPopup(true)}>
                      <span className="svgWidth svg-icon">
                        {/* Logout icon */}
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"><path opacity=".4" d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10Z" fill="currentColor"></path><path d="M12 13.75c.41 0 .75-.34.75-.75V8c0-.41-.34-.75-.75-.75s-.75.34-.75.75v5c0 .41.34.75.75.75ZM12.92 15.619c-.05-.12-.12-.23-.21-.33-.1-.09-.21-.16-.33-.21a1 1 0 0 0-.76 0c-.12.05-.23.12-.33.21-.09.1-.16.21-.21.33-.05.12-.08.25-.08.38s.03.26.08.38c.05.13.12.23.21.33.1.09.21.16.33.21.12.05.25.08.38.08s.26-.03.38-.08.23-.12.33-.21c.09-.1.16-.2.21-.33.05-.12.08-.25.08-.38s-.03-.26-.08-.38Z" fill="currentColor"></path></svg>
                      </span>
                      <span className="nav-text">
                        <FormattedMessage id="logout" />{" "}
                      </span>
                    </a>
                  </li> 
                </React.Fragment>
              )}
            </ul>
          </nav>
        </div>
      </aside>
    </React.Fragment>
  );
};

// Export component wrapped with HOCs and observer
export default UserProfilePopupHOC(
  
    TaxSettingPopupHOC(
      TimeSettingPopupHOC(
        BusinessTypeSettingPopupHOC(injectIntl(observer(CustomerLeftNavBar)))
      )
    )
  
);
